import { ArrowLeftIcon, Bars2Icon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import { Balance, ButtonGroup, Separator, Text, toolkitCx } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useMemo } from "react";

import { useTasteTestContributeWidgetTitle } from "../../../hooks/taste-test/useTasteTestContributeWidgetTitle";
import { useTasteTestWithdrawAmounts } from "../../../hooks/taste-test/useTasteTestWithdrawAmounts";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useTasteTestTx } from "../../../pages/TasteTestDetail/hooks/useTasteTestTx";
import { useProfileAppContext } from "../../../stores";
import {
  EOrderFlowState,
  EOrderTransactionBuilderState,
  OrderActions,
  useOrderContext,
} from "../../../stores/OrderContext";
import FadeInBasic from "../../FadeInView/FadeInBasic";
import TasteTestWithdrawDetails from "../TasteTestWithdrawDetails";
import TasteTestWithdrawWarning from "../TasteTestWithdrawWarning";

const TasteTestWithdrawFlow: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    state: {
      derived: { transaction },
      flowData: { orderConsent },
    },
    dispatch,
  } = useOrderContext();
  const {
    handlers: { setShowCurrentContribution },
    state: {
      tasteTestStatus: { isTasteTestInactive, isTasteTestReadOnly },
      tasteTestPersonalization,
    },
  } = useTasteTestContext();
  const { total: totalWithdrawAmount } = useTasteTestWithdrawAmounts();
  const widgetTitle = useTasteTestContributeWidgetTitle();
  const { submitTx } = useTasteTestTx({ type: "withdraw" });

  const isButtonDisabled =
    transaction?.state === EOrderTransactionBuilderState.BUILD || !orderConsent.checked;

  const buttonLabel = useMemo(() => {
    if (transaction?.state === EOrderTransactionBuilderState.BUILD) {
      return t("building", { ns: "common" });
    }

    return t("detail.widget.button.submit.withdraw");
  }, [t, transaction?.state]);

  const handleBackButton = useCallback(() => {
    setShowCurrentContribution(true);
    OrderActions.setOrderFlowState(EOrderFlowState.initial, dispatch);
    OrderActions.setOrderConsent({ checked: false }, dispatch);
  }, [dispatch]);

  return (
    <div
      className={cx("relative z-10", {
        "pointer-events-none blur-sm": isTasteTestInactive || isTasteTestReadOnly,
      })}
    >
      {/* -------------------------------------------------------------------------------------------------
       * Widget Title
       * -----------------------------------------------------------------------------------------------*/}
      <Text
        className={cx(
          "w-full text-center",
          "border-b border-b-gray-300 dark:border-b-gray-600",
          toolkitCx.layout.spacing.vertical.md
        )}
        tag="div"
        weight="medium"
        variant="info"
      >
        {widgetTitle}
      </Text>

      <div className={cx("flex w-full flex-col gap-6", toolkitCx.layout.spacing.both.md)}>
        <div className={cx("grid gap-2", toolkitCx.layout.spacing.horizontal.md)}>
          <TasteTestWithdrawDetails />

          <Separator orientation="horizontal" className={toolkitCx.layout.background.separator} />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bars2Icon
                className={cx("absolute -left-5 h-3 w-3 opacity-75", toolkitCx.layout.text.primary)}
              />
              <Text tag="span" size="sm" variant="muted">
                {t("detail.widget.currentContribution", {
                  context: "withdraw",
                })}
              </Text>
            </div>

            <Balance
              abbreviate={false}
              locale={currentLocale}
              privacy={privacyEnabled}
              showSymbol
              size="xl"
              gradient
              weight="medium"
              value={totalWithdrawAmount}
            />
          </div>
        </div>

        <TasteTestWithdrawWarning withConsent />
        <FadeInBasic className="sticky bottom-4 z-10" transitionDelay={150}>
          <ButtonGroup
            widthRatio={["3/4", "1/4"]}
            withBackdropBlur
            size="md"
            primaryButtonConfig={{
              buttonProps: {
                className: "hover:-translate-y-[2px] group",
                disabled: isButtonDisabled,
                onClick: () => submitTx(),
                style: { backgroundColor: tasteTestPersonalization?.primaryColor || "" },
              },
              label: buttonLabel,
            }}
            secondaryButtonConfig={{
              icon: ArrowLeftIcon,
              iconProps: {
                className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform),
              },
              label: t("back", { ns: "common" }),
              buttonProps: {
                className: "hover:-translate-y-[2px] group",
                onClick: handleBackButton,
              },
            }}
          />
        </FadeInBasic>
      </div>
    </div>
  );
};

export default TasteTestWithdrawFlow;
